import { data, Link, useFetcher, useNavigate } from "react-router";
import { toast } from "sonner";
import { Pagination } from "#app/components/navigation/Pagination.tsx";
import { SafeMarkdown } from "#app/components/safe-markdown.tsx";
import { ShareButton } from "#app/components/share-button.tsx";
import { VoteButtons } from "#app/components/vote-buttons.tsx";
import { useOptionalUser } from "#app/utils/user.ts";
import { getWordsByLetter } from "#app/utils/word.server.ts";
import { type Route } from "./+types/browse_.$letter.ts";

export async function loader({ params, request }: Route.LoaderArgs) {
  const { letter } = params;
  if (!letter) throw new Response("Not Found", { status: 404 });

  const url = new URL(request.url);
  const page = Math.max(1, Number(url.searchParams.get("page")) || 1);
  const limit = 20;

  const { words, totalCount } = await getWordsByLetter(letter, page, limit);
  const totalPages = Math.ceil(totalCount / limit);

  return data({
    words,
    letter: letter.toUpperCase(),
    pagination: { currentPage: page, totalPages, totalCount, limit },
  });
}

export const meta: Route.MetaFunction = ({ params }) => {
  const letter = params.letter?.toUpperCase() || "";
  const title =
    letter === "#"
      ? "Browse Words Starting with Numbers/Symbols - shonaslang"
      : `Browse Words Starting with ${letter} - shonaslang`;

  return [
    { title },
    {
      name: "description",
      content: `Shona slang words starting with ${letter}`,
    },
  ];
};

type LoaderWords = Awaited<ReturnType<typeof loader>>["data"]["words"];
type WordDef = LoaderWords[number]["definitions"][number];

function BrowseWordCard({
  word,
  definition,
}: {
  word: LoaderWords[number];
  definition: NonNullable<WordDef>;
}) {
  const user = useOptionalUser();
  const navigate = useNavigate();
  const fetcher = useFetcher();
  const isPending = fetcher.state !== "idle";
  const isOwnDefinition = user?.id === definition.author.id;

  const handleVote = (value: 1 | -1) => {
    if (!user || isOwnDefinition) return;
    void fetcher.submit(
      { intent: "vote", definitionId: definition.id, value: value.toString() },
      { method: "post", action: `/words/${word.slug}` },
    );
  };

  const promptSignIn = () => {
    toast("Sign in to vote", {
      description: "You need an account to vote on definitions.",
      action: { label: "Sign in", onClick: () => void navigate("/login") },
    });
  };

  return (
    <div className="rounded-lg border border-border bg-card p-6">
      <Link to={`/words/${word.slug}`} className="group block">
        <h3 className="mb-2 text-2xl font-semibold text-card-foreground group-hover:text-blue-600">
          {definition.definitionShona}
        </h3>
        <p className="mb-3 text-lg text-card-foreground">
          <strong>{definition.translationEn}</strong>
        </p>

        {definition.definitionEn && (
          <SafeMarkdown
            content={definition.definitionEn}
            className="mb-3 text-muted-foreground"
          />
        )}
      </Link>

      <div className="flex items-center justify-between text-sm text-muted-foreground">
        <span>
          by {definition.author.displayName || definition.author.username}
        </span>
        <div className="flex items-center gap-2">
          <VoteButtons
            up={definition.up}
            down={definition.down}
            onUpvote={() => handleVote(1)}
            onDownvote={() => handleVote(-1)}
            onDisabledClick={!user ? promptSignIn : undefined}
            disabled={isPending || !user || isOwnDefinition}
            size="sm"
          />
          <ShareButton
            definitionId={definition.id}
            word={definition.definitionShona}
            slug={word.slug}
            translation={definition.translationEn}
          />
        </div>
      </div>
    </div>
  );
}

export default function BrowseLetterPage({ loaderData }: Route.ComponentProps) {
  const { words, letter, pagination } = loaderData;

  return (
    <div className="container mx-auto max-w-4xl px-4 py-8">
      <div className="mb-8">
        <div className="mb-4 flex items-center justify-between">
          <h1 className="text-h1 text-foreground">
            Words starting with {letter}
          </h1>
          <Link to="/browse" className="text-blue-600 hover:text-blue-800">
            ← All letters
          </Link>
        </div>

        <p className="text-body-lg text-muted-foreground">
          {pagination.totalCount}{" "}
          {pagination.totalCount === 1 ? "word" : "words"} found
        </p>
      </div>

      {words.length === 0 ? (
        <div className="py-12 text-center">
          <p className="text-body-lg text-muted-foreground">
            No words found starting with "{letter}"
          </p>
          <Link
            to="/new"
            className="mt-4 inline-block text-blue-600 hover:text-blue-800"
          >
            Be the first to add a word!
          </Link>
        </div>
      ) : (
        <>
          <div className="space-y-4">
            {words.map((word) => {
              const definition = word.definitions[0];
              if (!definition) return null;

              return (
                <BrowseWordCard
                  key={word.id}
                  word={word}
                  definition={definition}
                />
              );
            })}
          </div>

          {pagination.totalPages > 1 && (
            <div className="mt-8 flex justify-center">
              <Pagination
                currentPage={pagination.currentPage}
                totalPages={pagination.totalPages}
                searchQuery=""
                basePath={`/browse/${letter.toLowerCase()}`}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}
